import { useEffect, useMemo, useState } from "react";
import {
  doc,
  onSnapshot,
  type DocumentData,
  type DocumentSnapshot,
  type FirestoreError,
} from "firebase/firestore";
import { db } from "@/firebase";
import { useAuthUser } from "@/hooks/useAuthUser";
import { getDefaultPermissions, type CaseMemberPermissions, type CaseMemberRole } from "@/types/caseMembership";

export type CaseDocument = {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  ownerId?: string;
  ownerUid?: string;
  partyId?: string;
  caseNumber?: string;
  status?: string;
  memberIds?: string[];
  memberUids?: string[];
  counselIds?: string[];
  evaluatorIds?: string[];
  roles?: Record<string, unknown>;
  [key: string]: unknown;
};

export type CaseMemberDocument = {
  id: string;
  caseId?: string;
  userId?: string;
  invitedEmail?: string;
  role?: string;
  permissions?: CaseMemberPermissions; // Added for Phase 1
  status?: string;
  invitedAt?: unknown;
  joinedAt?: unknown;
  removedAt?: unknown;
  [key: string]: unknown;
};

/**
 * Runtime access role as computed by deriveRole(). Includes all CaseMemberRole
 * values plus runtime-only states (owner, admin, invited) that are never written
 * to Firestore as membership role strings.
 */
export type CaseAccessRole =
  | "owner"
  | "member"
  | "admin"
  | "party"
  | "counsel"
  | "evaluator"
  | "legacy"
  | "professional"
  | "invited"
  // New Phase 1 roles:
  | "SRL_OWNER"
  | "PRO_PRIMARY"
  | "PRO_COLLAB"
  | "SRL_COLLAB"
  | "VIEW_ONLY";

export type CaseAccessDeniedReason =
  | "missing-case-id"
  | "unauthenticated"
  | "not-found"
  | "invited"
  | "permission-denied"
  | "no-access";

type UseCaseState = {
  loading: boolean;
  caseDoc: CaseDocument | null;
  error: FirestoreError | null;
  notFound: boolean;
};

type UseCaseMembershipState = {
  loading: boolean;
  membership: CaseMemberDocument | null;
  error: FirestoreError | null;
};

const initialCaseState: UseCaseState = {
  loading: false,
  caseDoc: null,
  error: null,
  notFound: false,
};

const initialMembershipState: UseCaseMembershipState = {
  loading: false,
  membership: null,
  error: null,
};

function normalizeCaseDocument(snapshot: DocumentSnapshot<DocumentData>): CaseDocument {
  const raw = snapshot.data() as Record<string, unknown>;
  const next: CaseDocument = {
    id: snapshot.id,
    ...raw,
  };

  const derivedTitle =
    typeof next.title === "string"
      ? next.title
      : typeof next.name === "string"
        ? next.name
        : undefined;

  if (derivedTitle && typeof next.title !== "string") {
    next.title = derivedTitle;
  }

  if (derivedTitle && typeof next.name !== "string") {
    next.name = derivedTitle;
  }

  return next;
}

function normalizeRole(rawRole: unknown): CaseAccessRole | null {
  if (typeof rawRole !== "string") {
    return null;
  }

  const role = rawRole.toLowerCase();

  switch (role) {
    case "owner":
      return "SRL_OWNER";
    case "admin":
    case "support":
    case "super":
      return "admin";
    case "member":
    case "collaborator":
    case "editor":
      return "member";
    case "party":
    case "plaintiff":
    case "defendant":
      return "party";
    case "counsel":
    case "attorney":
    case "lawyer":
      return "counsel";
    case "evaluator":
    case "reviewer":
      return "evaluator";
    case "legacy":
    case "viewer":
      return "legacy";
    case "professional":
    case "pro":
      return "professional";
    case "invited":
      return "invited";
    // Phase 1: New role types (case-sensitive match)
    case "srl_owner":
      return "SRL_OWNER";
    case "pro_primary":
      return "PRO_PRIMARY";
    case "pro_collab":
      return "PRO_COLLAB";
    case "srl_collab":
      return "SRL_COLLAB";
    case "view_only":
      return "VIEW_ONLY";
    default:
      return "member";
  }
}

function extractRoleFromCaseDoc(caseDoc: CaseDocument | null, uid: string | null): CaseAccessRole | null {
  if (!caseDoc || !uid) {
    return null;
  }

  if (caseDoc.ownerId === uid || caseDoc.ownerUid === uid) {
    return "SRL_OWNER";
  }

  if (Array.isArray(caseDoc.memberIds) && caseDoc.memberIds.includes(uid)) {
    return "member";
  }

  if (Array.isArray(caseDoc.memberUids) && caseDoc.memberUids.includes(uid)) {
    return "member";
  }

  if (caseDoc.partyId === uid) {
    return "party";
  }

  if (Array.isArray(caseDoc.counselIds) && caseDoc.counselIds.includes(uid)) {
    return "counsel";
  }

  if (Array.isArray(caseDoc.evaluatorIds) && caseDoc.evaluatorIds.includes(uid)) {
    return "evaluator";
  }

  if (caseDoc.roles && typeof caseDoc.roles === "object") {
    const entry = (caseDoc.roles as Record<string, unknown>)[uid];
    if (typeof entry === "string") {
      return normalizeRole(entry);
    }
    if (entry === true) {
      return "member";
    }
  }

  return null;
}

/**
 * Phase 4: Membership-first role derivation with explicit owner protection
 * 
 * Priority order:
 * 1. Owner check (explicit from case doc) - cannot be overridden
 * 2. Membership doc (cases/{caseId}/members/{uid}) - primary source for collaborators
 * 3. Legacy fallback (memberIds, counselIds, etc.) - backward compatibility
 * 4. Invited status (membership.status === 'invited')
 * 5. Admin override (platform admins)
 */
function deriveRole(
  uid: string | null,
  caseDoc: CaseDocument | null,
  membership: CaseMemberDocument | null,
  isAdminLike: boolean
): CaseAccessRole | null {
  if (!uid) {
    return null;
  }

  // PRIORITY 1: Explicit owner check (cannot be overridden by membership doc)
  // This ensures the case owner always has access even if membership doc is missing/misconfigured
  if (caseDoc && (caseDoc.ownerId === uid || caseDoc.ownerUid === uid)) {
    console.debug(`[useCaseAccess] User ${uid} is owner of case ${caseDoc.id}`);
    return "SRL_OWNER";
  }

  // PRIORITY 2: Membership doc (primary source for Pro collaborators and invited SRLs)
  if (membership) {
    const status = typeof membership.status === "string" ? membership.status : undefined;
    
    // Explicitly blocked statuses (removed, banned, etc.)
    if (status && status !== "active" && status !== "invited") {
      console.debug(`[useCaseAccess] User ${uid} has blocked status: ${status}`);
      return null;
    }

    // Active membership - use role from membership doc
    if (status === "active") {
      const normalized = normalizeRole(membership.role);
      if (normalized && normalized !== "invited") {
        console.debug(`[useCaseAccess] User ${uid} has active membership with role: ${normalized}`);
        return normalized;
      }
    }
  }

  // PRIORITY 3: Legacy fallback (memberIds, counselIds, etc.) for backward compatibility
  const fallback = extractRoleFromCaseDoc(caseDoc, uid);
  if (fallback) {
    console.debug(`[useCaseAccess] User ${uid} has legacy role: ${fallback}`);
    return fallback;
  }

  // PRIORITY 4: Invited status (pending invitation)
  if (membership && membership.status === "invited") {
    console.debug(`[useCaseAccess] User ${uid} has pending invitation`);
    return "invited";
  }

  // PRIORITY 5: Admin override (platform admins can access any case)
  if (isAdminLike) {
    console.debug(`[useCaseAccess] User ${uid} granted admin access`);
    return "admin";
  }

  // No access
  console.debug(`[useCaseAccess] User ${uid} has no access to case`);
  return null;
}

export type UseCaseResult = UseCaseState;
export type UseCaseMembershipResult = UseCaseMembershipState;

export interface CaseAccessState {
  loading: boolean;
  caseDoc: CaseDocument | null;
  membership: CaseMemberDocument | null;
  role: CaseAccessRole | null;
  permissions: CaseMemberPermissions | null; // Added for Phase 1
  hasAccess: boolean;
  notFound: boolean;
  error: FirestoreError | null;
  deniedReason: CaseAccessDeniedReason | null;
  userId: string | null;
}

export function useCase(caseId: string | null | undefined): UseCaseResult {
  const [state, setState] = useState<UseCaseState>(() => ({
    ...initialCaseState,
    loading: Boolean(caseId),
  }));

  useEffect(() => {
    if (!caseId) {
      setState(initialCaseState);
      return undefined;
    }

    setState({ loading: true, caseDoc: null, error: null, notFound: false });

    const caseRef = doc(db, "cases", caseId);
    const unsubscribe = onSnapshot(
      caseRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        // RACE FIX: Skip stale "not found" snapshots from Firestore's local cache.
        // Firebase may fire an empty fromCache snapshot before the server responds,
        // causing a false access denial before actual case data arrives.
        if (snapshot.metadata?.fromCache && !snapshot.exists()) {
          return;
        }

        if (!snapshot.exists()) {
          setState({ loading: false, caseDoc: null, error: null, notFound: true });
          return;
        }

        const normalized = normalizeCaseDocument(snapshot);
        setState({ loading: false, caseDoc: normalized, error: null, notFound: false });
      },
      (error) => {
        console.error("[useCase] Failed to load case", error);
        setState({ loading: false, caseDoc: null, error, notFound: false });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [caseId]);

  return state;
}

/**
 * Phase 4: Load membership doc from cases/{caseId}/members/{uid} subcollection
 * 
 * This is the primary source of truth for case access after Phase 2.
 * Membership docs contain:
 * - role: CaseMemberRole (SRL_OWNER, PRO_PRIMARY, PRO_COLLAB, SRL_COLLAB, VIEW_ONLY)
 * - permissions: CaseMemberPermissions (granular permission flags)
 * - status: 'active' | 'invited' | 'removed' | 'banned'
 * 
 * Returns null if no membership doc exists (falls back to legacy arrays in deriveRole).
 */
export function useCaseMembership(caseId: string | null | undefined, uid: string | null | undefined): UseCaseMembershipResult {
  // RACE FIX: Lazy initializer mirrors useCase pattern — ensures loading=true from
  // the very first render when caseId+uid are present, eliminating the window
  // between mount and the useEffect firing where loading would be false.
  const [state, setState] = useState<UseCaseMembershipState>(() => ({
    ...initialMembershipState,
    loading: Boolean(caseId && uid),
  }));

  useEffect(() => {
    if (!caseId || !uid) {
      setState(initialMembershipState);
      return undefined;
    }

    setState({ loading: true, membership: null, error: null });

    const memberRef = doc(db, "cases", caseId, "members", uid);
    const unsubscribe = onSnapshot(
      memberRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        // RACE FIX: Skip stale "not found" snapshots from Firestore's local cache.
        // Firebase may fire an empty fromCache snapshot before the server responds.
        // Ignoring these prevents false access denials on initial load when the
        // membership doc exists on the server but not yet in the local cache.
        if (snapshot.metadata?.fromCache && !snapshot.exists()) {
          return;
        }

        if (!snapshot.exists()) {
          setState({ loading: false, membership: null, error: null });
          return;
        }

        const data = snapshot.data() as Record<string, unknown>;
        const membership: CaseMemberDocument = {
          id: snapshot.id,
          ...data,
        };
        if (!membership.caseId) {
          membership.caseId = caseId;
        }
        if (!membership.userId) {
          membership.userId = uid;
        }
        
        // DEBUG: Log membership data for permission tracking
        console.log('[useCaseMembership] Loaded membership document:', {
          caseId,
          uid,
          role: membership.role,
          hasPermissions: Boolean(membership.permissions),
          permissions: membership.permissions,
          status: membership.status,
        });
        
        setState({ loading: false, membership, error: null });
      },
      (error) => {
        console.error("[useCaseMembership] Failed to load membership", error);
        setState({ loading: false, membership: null, error });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [caseId, uid]);

  return state;
}

export function useCaseAccess(caseId: string | null | undefined): CaseAccessState {
  const { status, firebaseUser, userDoc, loading: authLoading } = useAuthUser();
  const uid = firebaseUser?.uid ?? null;

  const caseState = useCase(caseId);
  const membershipState = useCaseMembership(caseId, uid);

  const rawState = useMemo(() => {
    if (!caseId) {
      return {
        loading: authLoading || caseState.loading || membershipState.loading,
        caseDoc: null,
        membership: null,
        role: null,
        permissions: null,
        hasAccess: false,
        notFound: false,
        error: caseState.error ?? membershipState.error,
        deniedReason: "missing-case-id" as CaseAccessDeniedReason,
        userId: uid,
      };
    }

    if (status !== "authed" || !uid) {
      return {
        loading: authLoading || caseState.loading || membershipState.loading,
        caseDoc: caseState.caseDoc,
        membership: membershipState.membership,
        role: null,
        permissions: null,
        hasAccess: false,
        notFound: caseState.notFound,
        error: caseState.error ?? membershipState.error,
        deniedReason: "unauthenticated" as CaseAccessDeniedReason,
        userId: uid,
      };
    }

    const isAdminLike = Boolean(
      userDoc?.isAdmin === true ||
        userDoc?.roles?.isAdmin === true ||
        userDoc?.roles?.isSuperAdmin === true ||
        userDoc?.roles?.admin === true ||
        userDoc?.roles?.super === true
    );

    const role = deriveRole(uid, caseState.caseDoc, membershipState.membership, isAdminLike);

    // Phase 1: Extract permissions from membership doc or derive from role
    let permissions: CaseMemberPermissions | null = null;
    if (role && role !== "invited") {
      // DEBUG: Log permission extraction
      console.log('[useCaseAccess] Extracting permissions:', {
        caseId,
        uid,
        role,
        hasMembership: Boolean(membershipState.membership),
        membershipPermissions: membershipState.membership?.permissions,
      });
      
      // Try to get permissions from membership doc first
      if (membershipState.membership?.permissions) {
        // PHASE 2 FIX: Always create a fresh object to ensure reference changes
        // This prevents React from thinking nothing changed due to same object reference
        permissions = { ...membershipState.membership.permissions };
        console.log('[useCaseAccess] Using membership permissions (fresh copy):', permissions);
      } else {
        // Fallback: derive default permissions from role
        const roleAsString = role as string;
        if (['SRL_OWNER', 'PRO_PRIMARY', 'PRO_COLLAB', 'SRL_COLLAB', 'VIEW_ONLY'].includes(roleAsString)) {
          permissions = getDefaultPermissions(roleAsString as CaseMemberRole);
        } else {
          // Legacy roles get member permissions (full access except delete)
          permissions = {
            canRead: true,
            canWriteJournal: true,
            canWriteForms: true,
            canWriteFilings: true,
            canWriteExhibits: true,
            canManageChecklists: true,
            canInvite: false,
            canRemoveMembers: false,
            canDelete: role === 'owner' || role === 'admin',
          };
        }
      }
    }

    let deniedReason: CaseAccessDeniedReason | null = null;
    let hasAccess = Boolean(role && role !== "invited");

    if (!role) {
      hasAccess = false;
      if (caseState.notFound) {
        deniedReason = "not-found" as CaseAccessDeniedReason;
      } else if (caseState.error?.code === "permission-denied" || membershipState.error?.code === "permission-denied") {
        deniedReason = "permission-denied" as CaseAccessDeniedReason;
      } else {
        deniedReason = "no-access" as CaseAccessDeniedReason;
      }
    } else if (role === "invited") {
      hasAccess = false;
      deniedReason = "invited" as CaseAccessDeniedReason;
    }

    // Diagnostic logging for access decisions
    console.debug(`[useCaseAccess] Access decision for case ${caseId}:`, {
      userId: uid,
      role,
      hasAccess,
      deniedReason,
      hasMembership: Boolean(membershipState.membership),
      membershipStatus: membershipState.membership?.status,
      isOwner: caseState.caseDoc && (caseState.caseDoc.ownerId === uid || caseState.caseDoc.ownerUid === uid),
    });

    const isLoading = authLoading || caseState.loading || membershipState.loading;
    
    return {
      loading: isLoading,
      caseDoc: caseState.caseDoc,
      membership: membershipState.membership,
      role,
      permissions,  // This will be delayed by wrapper below
      hasAccess,
      notFound: caseState.notFound,
      error: caseState.error ?? membershipState.error,
      deniedReason,
      userId: uid,
    };
  }, [
    authLoading,
    caseId,
    caseState.caseDoc,
    caseState.error,
    caseState.loading,
    caseState.notFound,
    membershipState.error,
    membershipState.loading,
    membershipState.membership,
    status,
    uid,
    userDoc?.isAdmin,
    userDoc?.roles?.admin,
    userDoc?.roles?.isAdmin,
    userDoc?.roles?.isSuperAdmin,
    userDoc?.roles?.super,
  ]);

  return rawState;
}

export function useCaseRole(caseId: string | null | undefined) {
  const { role, loading, hasAccess, deniedReason, error } = useCaseAccess(caseId);
  return { role, loading, hasAccess, deniedReason, error };
}
